import "./sellscreendialogs.scss";
import * as React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type ThunkDispatch } from "redux-thunk";
import { hideDialog } from "#/Actions/Dialogs/DialogActions.ts";
import { type SaleEmailReceiptOptions, sendEmailReceipt } from "#/Actions/SellScreen/SellScreenActions.tsx";
import Button from "#/Common/Buttons/Button.tsx";
import StatusText from "#/Common/Forms/StatusText.tsx";
import PopupManager from "#/Common/Popups/PopupManager.ts";
import { type InjectedDataSourceProps, withDataSource } from "#/Data/DataStores/WithDataSource.tsx";
import { isValidEmail } from "#/Utilities/Generic/Strings.ts";

const connector = connect();

interface Props extends ConnectedProps<typeof connector>, InjectedDataSourceProps {
    dispatch: ThunkDispatch<any, any, any>;
    options: SaleEmailReceiptOptions;
}

interface State {
    valid: Array<boolean>;
    emails: Array<string>;
}

export class RequestEmailReceipt extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        const emails = (this.props.options.defaultMail || "").split(";");

        this.state = {
            valid: Array.from<boolean>({ length: emails.length }).fill(false),
            emails,
        };

        this.sendEmail = this.sendEmail.bind(this);
        this.cancelRequest = this.cancelRequest.bind(this);
        this.handleAddEmail = this.handleAddEmail.bind(this);
    }

    protected sendEmail(e: React.MouseEvent): void {
        const emails = this.state.emails.filter((email, index) => {
            if(!this.state.valid[index]) {
                return false;
            }

            return !!email.trim();
        })
            .map(email => email.trim());

        if(!emails.length) {
            e.stopPropagation();
            PopupManager.error("Please enter a valid email.");

            return;
        }

        this.props.dispatch(hideDialog());
        this.props.dispatch(sendEmailReceipt({
            ...this.props.options,
            email: emails.join(";"),
        }));
    }

    protected cancelRequest(): void {
        this.props.dispatch(hideDialog());
    }

    protected handleChange(index: number, value: string): void {
        this.setState(prevState => ({
            emails: [
                ...prevState.emails.slice(0, index),
                value,
                ...prevState.emails.slice(index + 1),
            ],
        }));
    }

    protected handleAddEmail(): void {
        this.setState(prevState => ({
            emails: [
                ...prevState.emails,
                "",
            ],
        }));
    }

    protected checkValidity(index: number, value: string, callback: (valid: boolean) => void): void {
        const valid = isValidEmail(value.trim());

        this.setState(prevState => ({
            valid: [
                ...prevState.valid.slice(0, index),
                valid,
                ...prevState.valid.slice(index + 1),
            ],
        }));

        callback(valid);
    }

    public render(): React.ReactNode {
        return (
            <div className="request-email-receipt">
                <p>Please enter an email to send email to</p>
                {this.state.emails.map((email, index) => (
                    <StatusText
                        key={index}
                        type="email"
                        value={email}
                        onChange={this.handleChange.bind(this, index)}
                        checkStatus={this.checkValidity.bind(this, index)}
                        name={index.toString()}
                        errorText="Invalid email"
                        autoFocus
                    />
                ))}
                <Button
                    type="button"
                    classes={[ "button-add", "request-email-add" ]}
                    onClick={this.handleAddEmail}
                >
                    Add Email Address
                </Button>
                <div className="buttons">
                    <Button type="button" classes={[ "button-cancel" ]} onClick={this.cancelRequest}>Cancel</Button>
                    <Button type="button" classes={[ "button-primary" ]} onClick={this.sendEmail}>Send</Button>
                </div>
            </div>
        );
    }
}

export default connector(withDataSource(RequestEmailReceipt));
